/**
 * Match Administration's "Human reviewed" box (Ben, 2026-10-21): who
 * marked the match reviewed and when, with the full history on hover
 * (game_review_events, migration 118 — a row per change).
 *
 * READ-ONLY here (Ben, 2026-10-22): a match is signed off in Match Lab,
 * where the review happens and each change takes a comment
 * (ReviewSignOff). This box shows the state and links there; it cannot set
 * or clear it.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { ApiError, api } from '../lib/api'
import { useActionLog } from './actionLog'

type ReviewChange = { reviewed: boolean; by: string | null; at: string | null }
type ReviewState = {
  reviewed: boolean
  reviewed_by: string | null
  reviewed_at: string | null
  history: ReviewChange[]
  /** Every change ever made; history holds the latest of them. */
  history_total?: number
}

/** Only a well-formed review state is shown: anything else hides the box
 *  rather than taking the Match Administration page down with it. */
function asReviewState(d: unknown): ReviewState | null {
  const r = d as Partial<ReviewState> | null
  return r && typeof r.reviewed === 'boolean' && Array.isArray(r.history)
    ? r as ReviewState : null
}

// Date AND time: an audit trail is read for when, and several changes in
// one day must not read the same (SDET review of #836).
const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined,
    { year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit' }) : ''

export function ReviewToggle({ gameId }: { gameId: number }) {
  const { log } = useActionLog()
  const [state, setState] = useState<ReviewState | null>(null)

  useEffect(() => {
    let live = true
    api<unknown>(`/api/v1/opsqa/games/${gameId}/review`)
      .then((d) => { if (live) setState(asReviewState(d)) })
      .catch((e: unknown) => {
        if (!live) return
        setState(null)
        // "not reviewed" and "could not ask" must not look the same
        log('fail', `Human reviewed could not be read — ${e instanceof ApiError ? e.message : String(e)}`)
      })
    return () => { live = false }
  }, [gameId, log])

  if (!state) return null
  const more = (state.history_total ?? state.history.length) - state.history.length
  const history = [
    ...state.history.map((h) =>
      `${h.reviewed ? 'Reviewed' : 'Unreviewed'} by ${h.by ?? 'unknown'} · ${when(h.at)}`),
    ...(more > 0 ? [`… and ${more} earlier change${more === 1 ? '' : 's'}`] : []),
  ].join('\n')
  return (
    <span className="ls-flag">
      <label title={(history || 'Not reviewed yet') + '\nSet in Match Lab'}>
        <input type="checkbox" checked={state.reviewed} disabled readOnly />
        Human reviewed
        {state.reviewed && state.reviewed_by && (
          <span className="muted ls-flag__who">
            {' '}· {state.reviewed_by} · {when(state.reviewed_at)}
          </span>
        )}
      </label>
      {' '}<Link className="ls-flag__link" to={`/ops/match-lab/${gameId}`}
                 title="Review this match and sign it off in Match Lab">
        {state.reviewed ? 'Match Lab' : 'Review in Match Lab'}
      </Link>
    </span>
  )
}
